import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, CardContent, Button, List, ListItem, ListItemText, ListItemSecondaryAction, Chip, CircularProgress, Alert, Divider } from '@mui/material';
import { AccountBalanceWallet as WalletIcon, TrendingUp as TrendingUpIcon, History as HistoryIcon, Business as BusinessIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl, API_ENDPOINTS } from '../config/api';

interface Transaction {
  id: string;
  type: 'earned' | 'spent' | 'transferred' | 'rewarded' | 'deducted';
  amount: number;
  description: string;
  reason?: string;
  date: string;
  status: string;
  awardedBy?: {
    id: string;
    name: string;
    email: string;
  };
}

interface WalletData {
  balance: number;
  monthlyEarned: number;
  monthlySpent: number;
  transactions: Transaction[];
}

interface CompanyStats {
  totalPoints: number;
  totalUsers: number;
  activeUsers: number;
  totalMonthlyEarned: number;
  totalEarnedAllTime: number;
}

const Wallet: React.FC = () => {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const [walletData, setWalletData] = useState<WalletData>({
    balance: 0,
    monthlyEarned: 0,
    monthlySpent: 0,
    transactions: []
  });
  const [companyStats, setCompanyStats] = useState<CompanyStats>({
    totalPoints: 0,
    totalUsers: 0,
    activeUsers: 0,
    totalMonthlyEarned: 0,
    totalEarnedAllTime: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchWalletData();
  }, []);

  const fetchWalletData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch user's points balance
      const balanceResponse = await fetch(getApiUrl(`/points/balance/${user?.id}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!balanceResponse.ok) {
        throw new Error(t('wallet.failedToLoadData'));
      }

      const balanceData = await balanceResponse.json();
      
      // Fetch user's transaction history
      const transactionsResponse = await fetch(getApiUrl(`/points/transactions/${user?.id}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      let transactions: Transaction[] = [];
      if (transactionsResponse.ok) {
        const transactionsData = await transactionsResponse.json();
        transactions = transactionsData.transactions || [];
      }

      // Fetch company stats for context
      const companyResponse = await fetch(getApiUrl(API_ENDPOINTS.DISTRIBUTION), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      let companyData = {
        totalPoints: 0,
        totalUsers: 0,
        activeUsers: 0,
        totalMonthlyEarned: 0,
        totalEarnedAllTime: 0
      };

      if (companyResponse.ok) {
        const companyResponseData = await companyResponse.json();
        companyData = {
          totalPoints: companyResponseData.summary.totalPoints || 0,
          totalUsers: companyResponseData.summary.totalUsers || 0,
          activeUsers: companyResponseData.summary.activeUsers || 0,
          totalMonthlyEarned: companyResponseData.summary.totalMonthlyEarned || 0,
          totalEarnedAllTime: companyResponseData.summary.totalEarnedAllTime || 0
        };
      }

      // Calculate monthly stats from transactions
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      
      const monthlyEarned = transactions
        .filter(t => (t.type === 'earned' || t.type === 'rewarded') && new Date(t.date).getMonth() === currentMonth && new Date(t.date).getFullYear() === currentYear)
        .reduce((sum, t) => sum + t.amount, 0);
      
      const monthlySpent = transactions
        .filter(t => (t.type === 'spent' || t.type === 'deducted') && new Date(t.date).getMonth() === currentMonth && new Date(t.date).getFullYear() === currentYear)
        .reduce((sum, t) => sum + Math.abs(t.amount), 0);

      setWalletData({
        balance: balanceData.pointsBalance || 0,
        monthlyEarned,
        monthlySpent,
        transactions
      });

      setCompanyStats(companyData);

    } catch (err) {
      console.error('Error fetching wallet data:', err);
      setError(t('wallet.failedToLoadData'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
          Wallet
        </Typography>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        {t('wallet.title')}
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
        {/* Personal Balance Card */}
        <Card sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <WalletIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
            <Box>
              <Typography variant="h6" color="text.secondary">
                {t('wallet.yourPersonalBalance')}
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {walletData.balance.toLocaleString()} {t('wallet.points')}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button variant="contained" startIcon={<TrendingUpIcon />}>
              {t('wallet.viewAnalytics')}
            </Button>
            <Button variant="outlined" startIcon={<HistoryIcon />}>
              {t('wallet.transactionHistory')}
            </Button>
          </Box>
        </Card>

        {/* Quick Stats */}
        <Card sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('wallet.thisMonth')}
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('wallet.earned')}
            </Typography>
            <Typography variant="h5" color="success.main">
              +{walletData.monthlyEarned.toLocaleString()} {t('wallet.points')}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {t('wallet.spent')}
            </Typography>
            <Typography variant="h5" color="error.main">
              -{walletData.monthlySpent.toLocaleString()} {t('wallet.points')}
            </Typography>
          </Box>
        </Card>
      </Box>

      {/* Company Context */}
      <Card sx={{ mt: 3, p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <BusinessIcon sx={{ fontSize: 32, color: 'info.main', mr: 2 }} />
          <Typography variant="h6">
            {t('wallet.companyPointsOverview')}
          </Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 3 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {t('wallet.totalCompanyPoints')}
            </Typography>
            <Typography variant="h5" color="info.main" sx={{ fontWeight: 'bold' }}>
              {companyStats.totalPoints.toLocaleString()}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {t('wallet.yourShare')}
            </Typography>
            <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
              {companyStats.totalPoints > 0 ? ((walletData.balance / companyStats.totalPoints) * 100).toFixed(1) : '0'}%
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {t('wallet.activeUsers')}
            </Typography>
            <Typography variant="h5" color="success.main" sx={{ fontWeight: 'bold' }}>
              {companyStats.activeUsers}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {t('wallet.monthlyDistributed')}
            </Typography>
            <Typography variant="h5" color="warning.main" sx={{ fontWeight: 'bold' }}>
              {companyStats.totalMonthlyEarned.toLocaleString()}
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* Recent Transactions */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('wallet.recentTransactions')}
          </Typography>
          {walletData.transactions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                {t('wallet.noTransactionsFound')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('wallet.transactionHistoryDescription')}
              </Typography>
            </Box>
          ) : (
            <List>
              {walletData.transactions.slice(0, 10).map((transaction) => (
                <ListItem key={transaction.id} divider sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {transaction.description}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(transaction.date).toLocaleDateString()}
                      </Typography>
                      {transaction.reason && (
                        <Typography variant="body2" color="primary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                          <strong>{t('wallet.reason')}</strong> {transaction.reason}
                        </Typography>
                      )}
                      {transaction.awardedBy && (
                        <Typography variant="caption" color="text.secondary">
                          {t('wallet.awardedBy')} {transaction.awardedBy.name}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 'bold',
                          color: (transaction.type === 'earned' || transaction.type === 'rewarded') ? 'success.main' : 'error.main'
                        }}
                      >
                        {(transaction.type === 'earned' || transaction.type === 'rewarded') ? '+' : ''}{transaction.amount.toLocaleString()} {t('wallet.points')}
                      </Typography>
                      <Chip
                        label={transaction.status}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Wallet; 